/**
 * Admin Portal Consolidation Step 4 — public invite-acceptance page.
 *
 * UNAUTH route. Calls `GET /api/auth/admin-invite/lookup?token=` to
 * pre-render the inviter context, then renders one of two forms:
 *   - `requires_account_creation === true`  → name + password fields
 *   - existing account                       → password-only (server
 *     ignores the body's password if the user already has one — the
 *     accept endpoint flips is_admin and issues a new session JWT,
 *     re-using whatever credentials the user already has)
 *
 * On success the new session JWT lands in localStorage (via api.js
 * interceptor on AuthContext re-hydration) and the user is shoved to
 * /admin with a success toast.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PublicPageShell from "@/components/PublicPageShell";
import { Loader2, ShieldCheck, ShieldOff } from "lucide-react";

export default function AcceptAdminInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { setSession } = useAuth();

  const [lookup, setLookup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lookupError, setLookupError] = useState(null);

  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setLookupError("Missing invite token.");
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.get(
          `/auth/admin-invite/lookup?token=${encodeURIComponent(token)}`
        );
        if (cancelled) return;
        setLookup(data);
      } catch (e) {
        if (cancelled) return;
        setLookupError(
          formatApiErrorDetail(e.response?.data?.detail) ||
            "This invite is invalid, expired, or has already been used."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const needsAccount = !!lookup?.requires_account_creation;

  const validate = () => {
    const e = {};
    if (needsAccount) {
      if (!password || password.length < 8) e.password = "8+ characters required.";
      if (!name.trim()) e.name = "Required.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = { token };
      if (needsAccount) {
        payload.password = password;
        payload.name = name.trim();
      }
      const { data } = await api.post(
        "/auth/admin-invite/accept",
        payload
      );
      // Re-use AuthContext setter; uses positional (token, user) args.
      if (setSession) {
        setSession(data.access_token, data.user);
      } else {
        localStorage.setItem("pp_token", data.access_token);
      }
      toast.success("Admin access granted — welcome aboard!");
      navigate("/admin", { replace: true });
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) ||
          "Could not accept this invite."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const expiresLabel = useMemo(() => {
    if (!lookup?.expires_at) return null;
    try {
      const d = new Date(lookup.expires_at);
      return d.toLocaleString();
    } catch {
      return lookup.expires_at;
    }
  }, [lookup?.expires_at]);

  if (loading) {
    return (
      <PublicPageShell mainClass="flex items-center justify-center px-6 py-12">
        <Card className="rounded-2xl border border-pp-border bg-pp-surface p-8 text-center max-w-md w-full">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-brand-orange" />
          <p className="mt-4 text-sm text-pp-text-muted">Checking your invite&hellip;</p>
        </Card>
      </PublicPageShell>
    );
  }

  if (lookupError) {
    return (
      <PublicPageShell mainClass="flex items-center justify-center px-6 py-12">
        <Card
          className="rounded-2xl border border-pp-border bg-pp-surface p-8 text-center max-w-md w-full"
          data-testid="admin-invite-error"
        >
          <ShieldOff className="mx-auto h-10 w-10 text-brand-orange" aria-hidden="true" />
          <h1 className="mt-4 font-heading text-3xl font-bold text-pp-text">Invite unavailable</h1>
          <p className="mt-3 text-sm text-pp-text-muted">{lookupError}</p>
          <Button
            type="button"
            onClick={() => navigate("/", { replace: true })}
            className="mt-6 h-11 rounded-full bg-pp-purple px-6 text-white hover:bg-pp-purple-dark"
            data-testid="admin-invite-back"
          >
            Back to home
          </Button>
        </Card>
      </PublicPageShell>
    );
  }

  return (
    <PublicPageShell mainClass="flex items-center justify-center px-6 py-12">
      <Card
        className="rounded-2xl border border-pp-border bg-pp-surface p-8 max-w-md w-full"
        style={{ boxShadow: "var(--pp-card-shadow)" }}
        data-testid="admin-invite-card"
      >
        <div className="text-center">
          <ShieldCheck className="mx-auto h-10 w-10 text-brand-orange" aria-hidden="true" />
          <h1 className="mt-4 font-heading text-3xl font-bold text-pp-text">
            Accept your admin invite
          </h1>
          <p className="mt-2 text-sm text-pp-text-muted">
            <strong className="text-pp-text">{lookup?.inviter_name || lookup?.inviter_email}</strong>{" "}
            invited <strong className="text-pp-text">{lookup?.email}</strong>{" "}
            to administer PooPluckers.
          </p>
          {expiresLabel && (
            <p className="mt-1 text-xs text-pp-text-muted">
              Link expires {expiresLabel}
            </p>
          )}
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          {needsAccount && (
            <>
              <div>
                <Label htmlFor="invite-name" className="text-sm font-semibold text-pp-text">
                  Your name
                </Label>
                <Input
                  id="invite-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-2 h-11 rounded-full px-5"
                  data-testid="admin-invite-name-input"
                />
                {errors.name && (
                  <p className="mt-1 text-xs font-semibold text-red-600">{errors.name}</p>
                )}
              </div>
              <div>
                <Label htmlFor="invite-password" className="text-sm font-semibold text-pp-text">
                  Choose a password
                </Label>
                <Input
                  id="invite-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 h-11 rounded-full px-5"
                  data-testid="admin-invite-password-input"
                />
                {errors.password && (
                  <p className="mt-1 text-xs font-semibold text-red-600">{errors.password}</p>
                )}
              </div>
            </>
          )}

          {!needsAccount && (
            <p className="rounded-2xl bg-pp-surface2 px-4 py-3 text-sm text-pp-text-muted">
              You already have a PooPluckers account. Click the button below and we&rsquo;ll
              grant admin access — keep using your existing password.
            </p>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="h-12 w-full rounded-full bg-pp-purple text-white hover:bg-pp-purple-dark"
            data-testid="admin-invite-accept-button"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Granting access&hellip;
              </>
            ) : (
              "Accept & become admin"
            )}
          </Button>
        </form>
      </Card>
    </PublicPageShell>
  );
}
